import React, { memo, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  Box,
} from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import * as Papa from 'papaparse';
import { DropZone } from '../MonthlyShiftSlotSetup.styles';

interface ImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (csvData: string) => Promise<{ success: boolean; importedCount: number; errors: string[] }>;
  isLoading: boolean;
}

/**
 * CSVインポートダイアログコンポーネント
 *
 * - ドラッグ&ドロップエリア
 * - ファイル選択ボタン
 * - インポートボタン
 * - papaparse使用
 */
const ImportDialog: React.FC<ImportDialogProps> = memo(({
  open,
  onClose,
  onImport,
  isLoading,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<string>('');
  const [isDragActive, setIsDragActive] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<{
    success: boolean;
    importedCount: number;
    errors: string[];
  } | null>(null);

  const resetState = useCallback(() => {
    setFile(null);
    setCsvData('');
    setParseError(null);
    setImportResult(null);
    setIsDragActive(false);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const parseFile = useCallback((selectedFile: File) => {
    setParseError(null);
    setImportResult(null);

    Papa.parse(selectedFile, {
      complete: (results) => {
        if (results.errors.length > 0) {
          setParseError(`CSVパースエラー: ${results.errors[0].message}`);
          return;
        }

        // CSVデータを文字列として再構築
        const csvString = Papa.unparse(results.data);
        setCsvData(csvString);
        setFile(selectedFile);
      },
      error: (error) => {
        setParseError(`ファイル読み込みエラー: ${error.message}`);
      },
      encoding: 'UTF-8',
    });
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'text/csv') {
      parseFile(droppedFile);
    } else {
      setParseError('CSVファイルを選択してください');
    }
  }, [parseFile]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      parseFile(selectedFile);
    }
  }, [parseFile]);

  const handleImport = useCallback(async () => {
    if (!csvData) return;

    const result = await onImport(csvData);
    setImportResult(result);

    if (result.success && result.errors.length === 0) {
      setTimeout(() => {
        handleClose();
      }, 1500);
    }
  }, [csvData, onImport, handleClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>CSVインポート</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          シフト枠データのCSVファイルをインポートします。
        </Typography>

        <DropZone
          $isDragActive={isDragActive}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="body1" sx={{ mb: 1 }}>
            ここにCSVファイルをドラッグ&ドロップ
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            または
          </Typography>
          <Button
            variant="outlined"
            component="label"
          >
            ファイルを選択
            <input
              type="file"
              accept=".csv"
              hidden
              onChange={handleFileSelect}
            />
          </Button>
        </DropZone>

        {file && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="info">
              選択ファイル: {file.name}
            </Alert>
          </Box>
        )}

        {parseError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {parseError}
          </Alert>
        )}

        {importResult && (
          <>
            {importResult.importedCount > 0 && (
              <Alert severity="success" sx={{ mt: 2 }}>
                {importResult.importedCount}件のデータをインポートしました
              </Alert>
            )}
            {importResult.errors.length > 0 && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {importResult.errors.map((err, i) => (
                  <div key={i}>{err}</div>
                ))}
              </Alert>
            )}
            {importResult.success && importResult.importedCount === 0 && importResult.errors.length === 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                インポート対象のデータがありませんでした
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          キャンセル
        </Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={!csvData || isLoading}
        >
          {isLoading ? 'インポート中...' : 'インポート'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

ImportDialog.displayName = 'ImportDialog';

export default ImportDialog;
